import { EMAIL_PATTERN, useOrganizations } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { OrganizationRole } from '@consigli/types';
import { useCallback, useMemo, useState } from 'react';
import { HiOutlinePlus } from 'react-icons/hi2';

import { Checkbox, Fieldset, Select } from '@/components';

import { MultiEmailInput } from './multi-email-input';
import { ProjectAccessForm } from './project-access-form';
import { ProjectAccessList } from './project-access-list';
import { FormErrors, InviteFormData, ProjectAccess } from './types';
import { languageOptions, roleOptions } from './validation';

interface InviteFormFieldsProps {
  formData: InviteFormData;
  errors: FormErrors;
  onChange: (data: Partial<InviteFormData>) => void;
}

export const InviteFormFields = ({ formData, errors, onChange }: InviteFormFieldsProps) => {
  const { organizations = [] } = useOrganizations();
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectAccess | null>(null);

  const organizationOptions = useMemo(
    () =>
      organizations.map((org) => ({
        label: org.name,
        value: org.id,
      })),
    [organizations],
  );

  const selectedLanguageOption = useMemo(
    () => languageOptions.find((opt) => opt.value === formData.language) || null,
    [formData.language],
  );

  const selectedRoleOption = useMemo(
    () => roleOptions.find((opt) => opt.value === formData.organizationRole) || null,
    [formData.organizationRole],
  );

  const selectedOrgOption = useMemo(
    () => organizationOptions.find((opt) => opt.value === formData.organizationId) || null,
    [organizationOptions, formData.organizationId],
  );

  const showOrganizationSelect = useMemo(
    () =>
      formData.organizationRole === OrganizationRole.ADMIN ||
      (formData.organizationRole === OrganizationRole.USER && formData.canCreateProjects),
    [formData.organizationRole, formData.canCreateProjects],
  );

  const handleAddProject = useCallback(
    (project: ProjectAccess) => {
      const existingIndex = formData.projects.findIndex((p) => p.id === project.id);
      if (existingIndex >= 0) {
        // Update existing project
        const updatedProjects = [...formData.projects];
        updatedProjects[existingIndex] = project;
        onChange({ projects: updatedProjects });
      } else {
        // Add new project
        onChange({ projects: [...formData.projects, project] });
      }
      setEditingProject(null);
      setShowProjectForm(false);
    },
    [formData.projects, onChange],
  );

  const handleEditProject = useCallback((project: ProjectAccess) => {
    setEditingProject(project);
    setShowProjectForm(true);
  }, []);

  const handleRemoveProject = useCallback(
    (projectId: number) => {
      onChange({ projects: formData.projects.filter((p) => p.id !== projectId) });
    },
    [formData.projects, onChange],
  );

  const handleOpenProjectForm = useCallback(() => {
    setEditingProject(null);
    setShowProjectForm(true);
  }, []);

  const handleCancelProjectForm = useCallback(() => {
    setEditingProject(null);
    setShowProjectForm(false);
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <Fieldset legend="Recipients">
        <MultiEmailInput
          label="Emails"
          placeholder="Add an email and press Enter (e.g. alice@aecom.com)"
          value={formData.recipients}
          onChange={(recipients) => onChange({ recipients })}
          validateEmail={(email) => {
            if (!EMAIL_PATTERN.test(email)) return 'Valid email is required';
            if (!email.toLowerCase().endsWith('@aecom.com')) {
              return 'Only @aecom.com email addresses can be invited';
            }
            return undefined;
          }}
          formError={errors.recipients}
        />
      </Fieldset>

      <Fieldset legend="Role">
        <div className="space-y-1">
          <span className="block text-sm font-medium text-gray-700 mb-1">Organization Role</span>
          <Select
            options={roleOptions}
            value={selectedRoleOption ?? undefined}
            onValueChange={(option) => {
              onChange({
                organizationRole: option?.value ?? null,
                // Reset related fields when role changes
                canCreateProjects: false,
                projects: [],
                organizationId:
                  option?.value === OrganizationRole.ADMIN ? formData.organizationId : null,
              });
            }}
            placeholder="Select role"
            getOptionLabel={(opt) => opt?.label ?? ''}
            getOptionValue={(opt) => opt?.value ?? ''}
            className="w-full"
          />
          {errors.organizationRole && (
            <p className="mt-1 text-sm text-red-600">{errors.organizationRole}</p>
          )}
        </div>

        {formData.organizationRole === OrganizationRole.ADMIN && (
          <div className="mt-4 p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              <strong>Permissions:</strong> Admins can create projects.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              <strong>Project access:</strong> All projects are available to admins.
            </p>
          </div>
        )}

        {formData.organizationRole === OrganizationRole.USER && (
          <div className="mt-4 space-y-4">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Permissions</span>
              <Checkbox
                id="canCreateProjects"
                label="Can create projects"
                checked={formData.canCreateProjects}
                onCheckedChange={(checked) => {
                  onChange({
                    canCreateProjects: checked,
                    // Reset organization if unchecking
                    organizationId: checked ? formData.organizationId : null,
                  });
                }}
              />
              {errors.canCreateProjects && (
                <p className="mt-1 text-sm text-red-600">{errors.canCreateProjects}</p>
              )}
            </div>
          </div>
        )}
      </Fieldset>

      {showOrganizationSelect && (
        <Fieldset legend="Organization">
          <div className="space-y-1">
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Select Organization
            </span>
            <Select
              options={organizationOptions}
              value={selectedOrgOption ?? undefined}
              onValueChange={(option) => onChange({ organizationId: option?.value ?? null })}
              placeholder="Select organization"
              getOptionLabel={(opt) => opt?.label ?? ''}
              getOptionValue={(opt) => opt?.value ?? ''}
              className="w-full"
              showSearch
            />
            {errors.organizationId && (
              <p className="mt-1 text-sm text-red-600">{errors.organizationId}</p>
            )}
          </div>
        </Fieldset>
      )}

      {formData.organizationRole === OrganizationRole.USER && (
        <Fieldset legend="Project Access">
          <div className="space-y-4">
            <ProjectAccessList
              projects={formData.projects}
              onEdit={handleEditProject}
              onRemove={handleRemoveProject}
            />

            {showProjectForm ? (
              <ProjectAccessForm
                onAddProject={handleAddProject}
                onCancel={handleCancelProjectForm}
                existingProjects={formData.projects}
                editingProject={editingProject}
              />
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleOpenProjectForm}
                className="w-full"
              >
                <HiOutlinePlus className="h-4 w-4 mr-2" />
                Add Project Access
              </Button>
            )}

            {errors.projects && <p className="mt-1 text-sm text-red-600">{errors.projects}</p>}

            {formData.projects.length === 0 && !formData.canCreateProjects && !showProjectForm && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  Users need either project creation permission or specific project access.
                </p>
              </div>
            )}
          </div>
        </Fieldset>
      )}

      <Fieldset legend="Language">
        <div className="space-y-1">
          <span className="block text-sm font-medium text-gray-700 mb-1">Invite Language</span>
          <Select
            options={languageOptions}
            value={selectedLanguageOption ?? undefined}
            onValueChange={(option) => onChange({ language: option?.value ?? null })}
            placeholder="Select language"
            getOptionLabel={(opt) => opt?.label ?? ''}
            getOptionValue={(opt) => opt?.value ?? ''}
            className="w-full"
          />
          {errors.language && <p className="mt-1 text-sm text-red-600">{errors.language}</p>}
        </div>
      </Fieldset>
    </div>
  );
};
